import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, X, Sparkles } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "../../lib/api";
import { useCart, MIN_ORDER } from "../../context/CartContext";

const CartItem = ({ item, step, setQty, removeItem }) => (
  <div data-testid={`cart-item-${item.slug}`} className="flex gap-4">
    <img src={item.image} alt={item.name} className="w-16 h-16 rounded-xl object-cover border border-[#E7E0D8]" />
    <div className="flex-1 min-w-0">
      <p className="font-serif-display font-semibold text-sm leading-snug">{item.name}</p>
      <p className="font-mono-accent text-xs text-[#2E8A57] mt-0.5">{item.price.toFixed(2).replace(".", ",")} €</p>
      <div className="flex items-center gap-2 mt-2">
        <button data-testid={`cart-qty-minus-${item.slug}`} onClick={() => setQty(item.slug, item.quantity - step)} className="w-7 h-7 rounded-full border border-[#E7E0D8] flex items-center justify-center hover:bg-[#F5F0EB]"><Minus className="w-3 h-3" /></button>
        <span data-testid={`cart-qty-${item.slug}`} className="font-mono-accent text-sm w-6 text-center">{item.quantity}</span>
        <button data-testid={`cart-qty-plus-${item.slug}`} onClick={() => setQty(item.slug, item.quantity + step)} className="w-7 h-7 rounded-full border border-[#E7E0D8] flex items-center justify-center hover:bg-[#F5F0EB]"><Plus className="w-3 h-3" /></button>
        <button data-testid={`cart-remove-${item.slug}`} onClick={() => removeItem(item.slug)} className="ml-auto text-[#78716C] hover:text-[#2E8A57] transition-colors"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  </div>
);

const CartSuggestions = ({ items, missing }) => {
  const { addItem } = useCart();
  const [suggestions, setSuggestions] = useState([]);
  const slugs = items.map((i) => i.slug).sort().join(",");

  useEffect(() => {
    if (!slugs) return setSuggestions([]);
    api.get("/cart/suggestions", { params: { slugs, limit: 3 } }).then((r) => setSuggestions(r.data)).catch(() => {});
  }, [slugs]);

  if (items.length === 0 || suggestions.length === 0) return null;
  return (
    <div data-testid="cart-suggestions" className="border-t border-[#E7E0D8] pt-4 mt-2">
      <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#2E8A57] mb-3 flex items-center gap-1.5">
        <Sparkles className="w-3.5 h-3.5" /> Spesso comprati insieme{missing > 0 ? " — completa l'ordine" : ""}
      </p>
      <div className="space-y-2.5">
        {suggestions.map((p) => (
          <div key={p.slug} data-testid={`cart-suggestion-${p.slug}`} className="flex items-center gap-3 rounded-xl border border-[#E7E0D8] bg-white p-2">
            <img src={p.image} alt={p.name} className="w-11 h-11 rounded-lg object-cover" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium leading-snug truncate">{p.name}</p>
              <p className="font-mono-accent text-xs text-[#2E8A57]">{p.price.toFixed(2).replace(".", ",")} €</p>
            </div>
            <button
              data-testid={`cart-suggestion-add-${p.slug}`}
              onClick={() => addItem(p, 1)}
              aria-label={`Aggiungi ${p.name}`}
              className="btn-tuscan w-8 h-8 shrink-0 rounded-full flex items-center justify-center"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const CartDrawer = () => {
  const { items, setQty, removeItem, total, open, setOpen } = useCart();
  const navigate = useNavigate();
  const isB2B = !!sessionStorage.getItem("b2b_token");
  const minOrder = isB2B ? parseFloat(sessionStorage.getItem("b2b_min_order")) || MIN_ORDER : MIN_ORDER;
  const step = isB2B ? parseInt(sessionStorage.getItem("b2b_carton")) || 1 : 1;
  const missing = Math.max(0, minOrder - total);
  const pct = Math.min(100, (total / minOrder) * 100);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/40 z-[60]"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            data-testid="cart-overlay"
          />
          <motion.aside
            data-testid="cart-drawer"
            className="fixed right-0 top-0 bottom-0 w-full sm:w-[440px] bg-[#FDFBF7] z-[70] flex flex-col shadow-2xl"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center justify-between px-6 h-20 border-b border-[#E7E0D8]">
              <h2 className="font-serif-display font-bold text-xl">Il tuo carrello</h2>
              <button data-testid="cart-close-btn" onClick={() => setOpen(false)} className="w-9 h-9 rounded-full border border-[#E7E0D8] flex items-center justify-center hover:bg-[#F5F0EB] transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {items.length === 0 ? (
                <p data-testid="cart-empty-message" className="text-sm text-[#78716C] pt-10 text-center">Il carrello è vuoto. Aggiungi qualche piatto!</p>
              ) : (
                items.map((i) => <CartItem key={i.slug} item={i} step={step} setQty={setQty} removeItem={removeItem} />)
              )}
              {!isB2B && <CartSuggestions items={items} missing={missing} />}
            </div>

            <div className="border-t border-[#E7E0D8] px-6 py-5 space-y-4">
              <div>
                <div className="flex justify-between text-xs font-mono-accent mb-1.5">
                  <span className="text-[#78716C]">ORDINE MINIMO {isB2B ? "B2B " : ""}{minOrder.toFixed(0)}€{isB2B && step > 1 ? ` · CARTONI DA ${step}` : ""}</span>
                  {missing > 0 ? (
                    <span data-testid="min-order-missing" className="text-[#2E8A57] font-semibold">mancano {missing.toFixed(2).replace(".", ",")} €</span>
                  ) : (
                    <span className="text-[#3C6E53] font-semibold">✓ raggiunto</span>
                  )}
                </div>
                <div className="h-1.5 rounded-full bg-[#E7E0D8] overflow-hidden">
                  <div className="h-full bg-[#2E8A57] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                </div>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-sm text-[#78716C]">Totale</span>
                <span data-testid="cart-total" className="font-serif-display font-bold text-2xl text-[#1C1917]">{total.toFixed(2).replace(".", ",")} €</span>
              </div>
              <button
                data-testid="go-to-checkout-btn"
                disabled={items.length === 0 || missing > 0}
                onClick={() => { setOpen(false); navigate("/checkout"); }}
                className="btn-tuscan w-full py-4 rounded-full font-semibold text-sm tracking-wide disabled:opacity-40 disabled:pointer-events-none"
              >
                Procedi al checkout
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
